<template>
  <div class="container">
    <div class="row align-items-center">

      <!-- Type -->
      <div class="col-6 col-lg-2">
        <router-link
          class="code text-monospace"
          :to="`/shipments/${shipment.code}/detail`"
          v-if="shipment.code">
          <span ref="code">{{shipment.code}}</span>
        </router-link>
        <span
          role="button"
          class="copy"
          @click="() => onCopy(shipment.code)"
          v-if="shipment.code">
          <a-icon type="copy" class="text-primary" />
        </span>
        <div class="mt-3">
          <slot name="direction">
            <a-tag
              v-if="shipment.direction"
              :color="direction[shipment.direction].color">
              <a-icon :type="direction[shipment.direction].icon" />
            </a-tag>
          </slot>
          <span class="text-dark">
            {{$t(`enum.type.${shipment.type}`)}}
          </span>
        </div>
      </div>

      <!-- Order -->

      <div class="col-6 col-lg-2">
        <div class="font-size-12 text-dark text-monospace mb-2" v-if="shipment.ref">
          #<span class="user-select-all">{{shipment.ref}}</span>
        </div>
        <div>
          <a-avatar :src="`/resources/images/platforms/${shipment.platform}.png`" size="small"/>
          <span class="text-secondary">
            {{ $t(`enum.source.${shipment.platform}`) }}
          </span>
        </div>
        <div class="mt-1 font-size-12 d-none d-lg-block">
          <span v-if="shipment.method == 'EXPRESS'">⚡️</span>
          <span v-else>🇦🇷</span>
          {{ $t('shipment') }} {{ $t(`enum.method.${shipment.method}`) }}
        </div>
      </div>

      <!-- Origin -->

      <div class="col-6 col-lg-2 d-none d-lg-block" v-if="shipment.origin">
        <div class="text-dark mb-1 font-size-14 text-truncate">
          {{shipment.origin.contact.name}}
        </div>
        <div class="font-size-14 text-truncate user-select-all">
          {{shipment.origin.address.street}}
        </div>
        <div class="font-size-12 mb-1" :title="zone(shipment.origin)">
          {{shipment.origin.address.city}}, {{shipment.origin.address.state}}
        </div>
        <div class="font-size-12 text-muted text-lowercase">
          <span v-if="delay(shipment.origin)" class="text-danger">
            <a-icon type="fire" />
            {{ delay(shipment.origin) }}
          </span>
          <template v-else-if="shipment.origin.schedule">
            <a-icon type="calendar" />
            {{shipment.origin.schedule.max | datetime}}
          </template>
          <template v-else>
            <a-icon type="calendar" />
            {{$t('unscheduled')}}
          </template>
        </div>
      </div>

      <!-- Destination -->

      <div class="col-6 col-lg-2">
        <div class="text-dark mb-1 font-size-14 text-truncate d-none d-lg-block">
          <span v-if="shipment.destination.contact">
            {{shipment.destination.contact.name}}
          </span>
          <span v-else>****</span>
        </div>
        <div class="font-size-14 text-truncate user-select-all">
          {{shipment.destination.address.street}}
        </div>
        <div class="font-size-12 mb-1" :title="zone(shipment.destination)">
          {{shipment.destination.address.city}}, {{shipment.destination.address.state}}
        </div>
        <div class="font-size-12 text-muted text-lowercase d-none d-lg-block">
          <span v-if="delay(shipment.destination)" class="text-danger">
            <a-icon type="fire" />
            {{ delay(shipment.destination) }}
          </span>
          <template v-else-if="shipment.destination.schedule">
            <a-icon type="calendar" />
            {{shipment.destination.schedule.max | datetime}}
          </template>
          <template v-else>
            <a-icon type="calendar" />
            {{$t('unscheduled')}}
          </template>
        </div>
      </div>

      <!-- Status -->

      <div class="col-6 col-lg-2">
        <slot name="status">
          <div class="row">
            <div class="col-2 mt-1">
              <a-avatar
                class="pt-1"
                :style="`backgroundColor:${state.style.color}`">
                <i class="font-size-18 fe" :class="state.style.icon"/>
              </a-avatar>
            </div>
            <div class="col-8 ml-2">
              <div class="text-dark font-size-16">
                {{ state.title }}
              </div>
              <div class="font-size-12 font-weight-light">
                {{ state.subtitle }}
              </div>
              <div class="font-size-12">
                {{ shipment.promoted | datetime }}
              </div>
              <j-courier :value="shipment.courier" />
            </div>
          </div>
        </slot>
      </div>

      <!-- Action -->

      <div class="col-6 col-lg-2 d-none d-lg-block">
        <slot name="cta"></slot>
      </div>

    </div>
  </div>
</template>
<script>
import acl from '@/auth/acl'
import moment from 'moment'
import JCourier from '@/views/shared/labels/courier'
import { status, direction } from '@/utils/styles'

export default {
  name: 'Shipment',
  props: ['shipment'],
  components: {
    JCourier,
  },
  data: function () {
    return {
      status,
      direction,
    }
  },
  computed: {
    state() {
      // prepare the default values
      const key = this.shipment.sub_status || this.shipment.status
      const title = this.$t(`enum.status.${key}`)
      let subtitle = null
      let style = status[key]
      if (this.shipment.status === 'TO_COLLECT') {
        // show labeling info
        if (this.shipment.printed) {
          subtitle = this.$t('msg.shipment.label.printed')
          style = status.PRINTED
        } else {
          subtitle = this.$t('msg.shipment.label.pending')
        }
      } else if (this.shipment.status.startsWith('OUT')) {
        // show in transit info
        subtitle = this.$t(`enum.status.${this.shipment.status}`)
      } else if (this.shipment.sub_status === 'BLOCKED') {
        // show issue on blocked
        subtitle = this.$t(`enum.issue.${this.shipment.issue}`)
      }
      return {
        title: title,
        subtitle: subtitle,
        style: style,
      }
    },
  },
  methods: {
    zone(point) {
      return point.zone
        ? `${this.$t('zone')} ${point.zone.rate}`
        : this.$t('msg.undefined', { name: this.$t('zone') })
    },
    delay(point) {
      const now = moment()
      if (
        this.shipment.started &&
        !this.shipment.finished &&
        this.shipment.visits === 0 &&
        this.$auth.granted(acl.shipments.monitor) &&
        point.schedule &&
        point.schedule.max &&
        moment(point.schedule.max).isBefore(now)) {
        return moment(point.schedule.max).fromNow()
      }
      return null
    },
    onCopy(code) {
      navigator.clipboard.writeText(code)
      this.$message.success(this.$t('msg.copied'))
    },
  },
}
</script>
<style lang="scss">
.code {
  color: black;
  font-size: 20px;
  font-weight: 600;
}
.copy {
  display: inline-flex;
  margin-left: 5px;
}
</style>
